// =====================================================================
// 资讯文章正文（占位内容，按文章 id 索引）
// ---------------------------------------------------------------------
// block 类型：
//   { type:'p',       text }                        段落
//   { type:'h2',      text }                        小节标题（自动生成目录）
//   { type:'ul',      items:[...] }                 无序列表
//   { type:'quote',   text }                        引用
//   { type:'stat',    items:[{value,label}] }       数据条
//   { type:'callout', title, text }                 提示框
//   { type:'img',     caption }                     图片占位
// 作者 / 封面配色也放在这里。后续替换为真实文章时只改本文件即可。
// =====================================================================

export const blogArticles = {
  en: {
    'after-hours-calls': {
      author: {
        name: 'Mia Chen',
        role: 'Customer Research',
      },
      cover: 'blue',
      body: [
        {
          type: 'p',
          text: 'Rather than guessing how many orders you lost, start by seeing which calls nobody picked up.',
        },
        {
          type: 'p',
          text: 'When your team in China clocks off, a U.S. customer may have just finished their day — and be ready to confirm a model number, a delivery arrangement, or the last question before buying. Your site lists a phone number. The phone rings. Nobody answers.',
        },
        {
          type: 'p',
          text: 'The more useful question is not whether that was a lost sale. It is how many calls like this happen, what they ask about, and whether anyone followed up. Answer those, and the phone stops being just a contact detail and becomes a customer touchpoint you can actually manage.',
        },
        {
          type: 'h2',
          text: 'Cross-border operations leave a gap nobody owns',
        },
        {
          type: 'p',
          text: 'Cross-border sellers are used to working around time zones: ads run on the target market’s schedule, orders arrive on local time — but support is usually staffed to China’s working hours. When a customer dials the number on your website, packaging or other owned channel, the daytime process may not be online any more.',
        },
        {
          type: 'p',
          text: 'Not every one of those calls is about closing a sale. Some ask about compatibility, some chase a shipment, some just want to confirm opening hours. Counting every missed call as a lost order overstates the opportunity; writing them all off as noise loses people who genuinely needed help.',
        },
        {
          type: 'img',
          src: '/blog/after-hours-calls-by-hour.png',
          caption: 'A two-to-four week call log, split by U.S. local time.',
        },
        {
          type: 'h2',
          text: 'Measure the gap before you decide it is worth closing',
        },
        {
          type: 'p',
          text: 'A simple starting point is to log calls for two to four weeks: volume by U.S. local time, share missed, repeat callers, topics, and whether contact was ever re-established. For calls you did answer, note whether a specific product came up, whether a human had to take over, and whether the customer agreed to be contacted again.',
        },
        {
          type: 'ul',
          items: ['Volume by U.S. local time — when the gap actually is.', 'Share missed, and how often the same number calls back.', 'Topics: compatibility, shipping, after-sales, or just opening hours.', 'Whether contact was re-established, and whether a human was needed.'],
        },
        {
          type: 'p',
          text: 'If there is almost no real call volume outside working hours, fix how the number is presented and guided on your site first. If product or after-sales questions keep repeating, then look at extended staffing, voicemail, AI reception, or some combination. The data should make that choice, not a hunch.',
        },
        {
          type: 'h2',
          text: 'Three things AI voice should get right first',
        },
        {
          type: 'ul',
          items: ['Catch the basic questions. The AI answers from the FAQ, product materials and service rules you have already approved — and when a model or policy is uncertain, it asks a follow-up or says plainly that it cannot confirm.', 'Leave behind something usable. Call time, the customer’s question, any model numbers mentioned and what is still open, written up so the team can read it the next morning. "Someone called" is not a record.', 'Hand the hard cases to a person. Order status, refunds, specific commitments or missing documents should come with a clear next step — not an unverified answer invented to finish the conversation. SMS, tickets and callbacks only where the capability is live, the customer agreed, and the rules allow it.'],
        },
        {
          type: 'img',
          src: '/blog/after-hours-calls-three-outcomes.png',
          caption: 'One call, three possible end states: resolved, ticketed, or a link sent.',
        },
        {
          type: 'h2',
          text: 'Start the experiment with one real call',
        },
        {
          type: 'p',
          text: 'Pick your ten most common call topics and prepare accurate product data plus the boundaries of what may be answered. Test model recognition, follow-up questions and human hand-off using real phrasings. After you go live, review the calls that did not get resolved. Getting one small, common flow working is more reliable than promising to replace round-the-clock support on day one.',
        },
        {
          type: 'p',
          text: 'The value of a North American number is not that you have a number. It is that when a customer gets through, the problem is understood, written down, and picked up again if it needs to be.',
        },
        {
          type: 'callout',
          title: 'The point',
          text: 'Whether missed calls cost you revenue is a question for data. Whether calls get picked up is something you can start managing today.',
        },
      ],
    },

    'local-number-trust': {
      author: {
        name: 'Daniel Wu',
        role: 'Market Research',
      },
      cover: 'amber',
      body: [
        {
          type: 'p',
          text: 'Marketplace response time is part of platform rules. A brand phone line is a different customer channel. Both need an owner.',
        },
        {
          type: 'p',
          text: 'When budget is tight, it is tempting to point one AI phone number at every customer question — fewer night shifts, better service metrics. The idea has appeal, but it starts from a misunderstanding: marketplace messages and your own brand phone line are usually recorded in different places and governed by different rules. Picking up a call is not the same as replying to a marketplace chat.',
        },
        {
          type: 'p',
          text: 'The two channels can share product knowledge and handling procedures. They cannot share responsibility. This matters especially for sellers on Amazon or TikTok Shop, where messages sitting in the seller back office still have to be handled the way the platform requires.',
        },
        {
          type: 'h2',
          text: 'Amazon: check the eligibility and the scoring first',
        },
        {
          type: 'p',
          text: 'Amazon has said that for orders the seller ships and supports directly, three customer service metrics can earn up to 12 points toward the Account Health Rating (AHR): buyer contact rate below 0.50%, average contact response time below 12 hours, and buyer dissatisfaction rate below 4.5%. Amazon also states that falling short of these thresholds does not by itself restrict selling privileges. Check your own numbers in Feedback Manager — that is the authoritative view.',
        },
        {
          type: 'stat',
          items: [
            {
              value: '< 0.50%',
              label: 'buyer contact rate',
            },
            {
              value: '< 12 h',
              label: 'average contact response time',
            },
            {
              value: '< 4.5%',
              label: 'buyer dissatisfaction rate',
            },
          ],
        },
        {
          type: 'p',
          text: 'That response time applies to buyer messages, not to how fast your website phone line is answered. So "the AI answered the phone" cannot be repackaged as "we earned AHR points automatically", and this scoring mechanism is not the same as "one missed call closes your store".',
        },
        {
          type: 'h2',
          text: 'TikTok Shop: what is measured is a meaningful reply in Shop Chat',
        },
        {
          type: 'p',
          text: 'TikTok Shop U.S. customer service policy requires that, over the previous seven calendar days, at least 90% of chats a customer started and that need human handling receive a meaningful reply within 24 hours. A bare "hello" does not count as a meaningful reply, and pure automation does not replace a chat that needs the seller to act. Falling short can trigger warnings or further action — check the official policy and your shop notifications for the specifics.',
        },
        {
          type: 'p',
          text: 'TikTok Shop AHR also reflects a range of policy and fulfilment metrics. Crossing a threshold can lead to platform measures, but a single late reply cannot be blamed for every point lost. A brand phone line does not replace Shop Chat, and off-platform contact cannot be used to route around platform transactions or after-sales processes.',
        },
        {
          type: 'img',
          src: '/blog/local-number-trust-two-lines.png',
          caption: 'Two service lines, two sets of metrics.',
        },
        {
          type: 'h2',
          text: 'Staff and review the two lines separately',
        },
        {
          type: 'p',
          text: 'Marketplace side: name the people who check messages, orders and complaints; set back-office reminders; give a concrete reply to anything that needs a human, and record the escalation and the deadline. Automated replies can help triage and cut repeat enquiries — do not assume the platform counts them as a human reply.',
        },
        {
          type: 'p',
          text: 'Owned channels: measure call volume, the hours nobody covers and the recurring questions across your website and other touchpoints. AI voice can handle first-line reception, capture the need and leave a handover record. Order, refund and warranty questions should follow the rules your business has verified and the relevant platform process.',
        },
        {
          type: 'p',
          text: 'Both lines can draw on one approved product knowledge base, but they must be measured separately. One looks at pending, overdue and dissatisfied on the platform. The other looks at calls answered, questions recognised, human follow-up, and consent for later contact.',
        },
        {
          type: 'h2',
          text: 'Three checks you can run today',
        },
        {
          type: 'ul',
          items: ['Open the seller back office and find the service metrics and pending messages that actually apply to your store.', 'Pull the last two weeks of website or brand phone records and find the hours nobody answered.', 'Name one person responsible for moving a complex enquiry from the AI or the duty agent to whoever can actually resolve it.'],
        },
        {
          type: 'p',
          text: 'Controlling support cost should not become giving up on support. Get the platform obligations right first, then use the tools that fit to cover the gap on your own channels. That order is far more workable.',
        },
        {
          type: 'h2',
          text: 'Sources',
        },
        {
          type: 'ul',
          items: ['Amazon Seller Forums — Your Account Health Rating now rewards great customer service', 'TikTok Shop Academy — Customer Service Policy', 'TikTok Shop Academy — Requirements for the 24-Hour Response Rate Metric', 'TikTok Shop Academy — Account Health Rating Requirements', 'Amazon Seller Central — Communication Guidelines'],
        },
        {
          type: 'callout',
          title: 'The point',
          text: 'A phone call can catch a customer. It cannot replace the marketplace message that is still unanswered.',
        },
      ],
    },

    'product-faq': {
      author: {
        name: 'Sara Lin',
        role: 'Product',
      },
      cover: 'teal',
      body: [
        {
          type: 'p',
          text: 'Sourcing and ads bring people to the product. A precise pre-sales answer is what helps them keep deciding.',
        },
        {
          type: 'p',
          text: 'The growth playbook every cross-border seller knows is testing products, buying traffic, tuning pages and hunting the next winner. All of that matters. But once a customer asks "will this fit my device" or "which generation does this cover fit", the question has changed: what they need right now is not another ad, it is an answer they can trust.',
        },
        {
          type: 'p',
          text: 'High intent is not the same as a guaranteed sale. It means the customer has named the thing standing in the way of their decision. Handle that obstacle accurately and the conversation can continue. Get the model wrong and an enthusiastic recommendation turns into a return and a broken promise.',
        },
        {
          type: 'h2',
          text: 'A compatibility question often matters more than a promotion',
        },
        {
          type: 'p',
          text: 'Say a customer is looking for a dust cover for a piece of music equipment. The catalogue has similar models, plus different generations and sizes. The customer is asking "does this fit". If the agent mishears the model, no amount of fluent talk about materials and colours solves the actual problem.',
        },
        {
          type: 'p',
          text: 'This is an illustration of the flow, not a specific customer case. The right order is: confirm the device model and version, look up the verified fitment data, then answer "confirmed compatible", "confirmed not compatible", or "we cannot verify this yet". Anything unverifiable becomes an open question for a human.',
        },
        {
          type: 'img',
          src: '/blog/product-faq-three-answers.png',
          caption: 'The three-answer rule: yes, no, or cannot verify yet.',
        },
        {
          type: 'h2',
          text: 'Customer questions are a stress test for your product data',
        },
        {
          type: 'p',
          text: 'A pre-sales team can sort enquiries into three buckets: standard questions answerable directly, questions that need one missing piece of information, and questions your materials cannot answer at all and a human must take. Size, colour and compatible model look simple — and they are exactly where an incomplete product table or a misheard model number sends everything off course.',
        },
        {
          type: 'ul',
          items: ['Answerable directly: sizing, colour, delivery window, standard policy.', 'Needs one more detail: "which generation is your unit?" before anything can be confirmed.', 'Cannot be answered: missing data, a special case, or something only a person should commit to.'],
        },
        {
          type: 'p',
          text: 'If the same model is misheard every time, fix the recognition terms and product aliases. If customers keep asking about "waterproof" while your copy only says "splash resistant", rewrite the wording you actually use. If the AI repeatedly fails to find a product that is in fact listed, check the product data — do not just rewrite a sentence of script.',
        },
        {
          type: 'h2',
          text: 'Close the loop on pre-sales reception',
        },
        {
          type: 'ul',
          items: ['Identify what the customer is actually trying to solve.', 'Ask a follow-up when the model or the use case is missing.', 'Answer from verified product data, not from inference.', 'When recommending, say what the recommendation is based on.', 'Hand off to a human whenever it cannot be confirmed.', 'Keep the question, the model mentioned and the outcome for the team to review.'],
        },
        {
          type: 'p',
          text: 'When you evaluate this, do not stop at "how many calls did the AI answer". The more useful numbers are: what share of calls ended with a confirmed model, how often a follow-up was needed, how many hand-offs were caused by missing data, and how many wrong recommendations a human had to correct. If you can tie calls to orders, look at the real change; if you cannot, do not claim the calls produced conversions.',
        },
        {
          type: 'callout',
          title: 'What to measure',
          text: 'Share of calls ending with a confirmed model. Hand-offs caused by missing data. Wrong recommendations a human had to catch.',
        },
        {
          type: 'h2',
          text: 'Start with the product that is easiest to get wrong',
        },
        {
          type: 'p',
          text: 'Pick one category with complex models that customers ask about constantly. Write down the models, the compatibility relationships, the sizes, the SKUs and the phrases you must not use. Prepare ten to twenty real customer phrasings covering accents, generations, near-miss model numbers and second corrections. Feed every failing sentence back into the data or the rules, then retest the same class of question.',
        },
        {
          type: 'p',
          text: 'That is more reliable than importing the entire catalogue at once and hoping the AI teaches itself to sell. The goal of pre-sales service is to remove doubt from the customer’s decision — without creating a new promise you cannot keep.',
        },
        {
          type: 'callout',
          title: 'The point',
          text: 'Ads create the opportunity. An accurate answer is what lets the customer feel safe enough to keep asking.',
        },
      ],
    },

    'timezone-gap': {
      author: { name: 'Mia Chen', role: 'Customer Research' },
      cover: 'blue',
      body: [
        { type: 'p', text: 'The time-zone gap is not a scheduling problem. It is a trust problem that shows up at the worst possible hour.' },
        { type: 'h2', text: 'What the gap actually looks like' },
        { type: 'p', text: 'A seller in Shenzhen closes the laptop at 7pm. For a customer in Los Angeles, that is 4am — but the order was placed at 10am their time, and the question is still open. By the time anyone is awake on both sides, the customer has already decided something.' },
        { type: 'h2', text: 'Three things a U.S. number changes' },
        { type: 'ul', items: [
          'The customer can call at their own hour and reach something.',
          'The follow-up text arrives while the question is still warm.',
          'The ticket is created with a timestamp that matches the customer\u2019s day, not yours.',
        ] },
        { type: 'stat', items: [
          { value: '9h', label: 'typical overlap loss' },
          { value: '62%', label: 'of after-hours calls go unanswered' },
          { value: '1', label: 'number closes the gap' },
        ] },
        { type: 'h2', text: 'Where to start' },
        { type: 'p', text: 'Start with the hours you are losing, not the hours you are covering. Pull last month\u2019s calls, sort by time, and find the window where volume is highest and coverage is lowest. That window is where the number pays for itself first.' },
        { type: 'quote', text: 'You cannot move your team\u2019s time zone. You can stop making the customer wait for it.' },
      ],
    },

    '100-calls-test': {
      author: { name: 'Sara Lin', role: 'Product' },
      cover: 'amber',
      body: [
        { type: 'p', text: 'We ran 100 AI reception calls against a live product catalog and scored every one. Three failure patterns accounted for most of the misses.' },
        { type: 'h2', text: 'Failure 1: the question that needs a human' },
        { type: 'p', text: 'Compatibility and warranty disputes are not reception problems. When the AI tried to resolve them, it produced confident answers that were sometimes wrong. The fix was not a better answer — it was a faster handoff.' },
        { type: 'h2', text: 'Failure 2: two questions in one breath' },
        { type: 'p', text: 'Callers often ask two things at once. Models tend to answer the first and drop the second. We now force a confirmation step before the call ends.' },
        { type: 'h2', text: 'Failure 3: the number nobody expected' },
        { type: 'p', text: 'Order numbers read aloud are noisy. Digit-by-digit confirmation cut the error rate sharply.' },
        { type: 'stat', items: [
          { value: '100', label: 'calls scored' },
          { value: '3', label: 'failure patterns' },
          { value: '1', label: 'fix per pattern' },
        ] },
        { type: 'quote', text: 'Most AI reception misses are not language problems. They are handoff problems.' },
        { type: 'h2', text: 'The takeaway' },
        { type: 'p', text: 'Do not optimize the answer. Optimize the exit. Every call should end in a state someone can act on.' },
      ],
    },

    'four-rings': {
      author: { name: 'Daniel Wu', role: 'Market Research' },
      cover: 'teal',
      body: [
        { type: 'p', text: 'Four rings is about 20 seconds. For a U.S. caller, that is roughly the point where "this might be busy" turns into "this place does not want my call."' },
        { type: 'h2', text: 'Why four rings is the line' },
        { type: 'p', text: 'Behavioral studies on phone abandonment put the drop-off steeply after the fourth ring. It is not a hard rule, but it is a reliable default.' },
        { type: 'h2', text: 'What a hang-up costs' },
        { type: 'ul', items: [
          'The order does not happen today — and often not at all.',
          'The customer calls a competitor who picked up.',
          'You never see the loss in your analytics, because a hang-up leaves no record.',
        ] },
        { type: 'stat', items: [
          { value: '~20s', label: 'to the fourth ring' },
          { value: '1 in 3', label: 'abandon before it' },
          { value: '0', label: 'trace in your logs' },
        ] },
        { type: 'h2', text: 'The uncomfortable part' },
        { type: 'p', text: 'You cannot measure the calls you never got. That is exactly why the cost stays invisible on most dashboards.' },
        { type: 'quote', text: 'A missed call is not a missed call. It is a decision the customer made about you.' },
      ],
    },

    'q3-release': {
      author: { name: 'Alex Zhao', role: 'Product' },
      cover: 'blue',
      body: [
        { type: 'p', text: 'Three things shipped this quarter, and one thing we are deliberately holding back.' },
        { type: 'h2', text: 'Outbound reminder calls' },
        { type: 'p', text: 'You can now schedule a reminder call for orders that need a nudge — a delivery window, a pending return, an unpaid balance. It uses the same voice and the same number as your reception line, so it does not feel like a different company.' },
        { type: 'h2', text: 'Pricing update' },
        { type: 'p', text: 'Number rental and per-minute rates are unchanged. We added a bundled minute tier for accounts that consistently run above the base plan, which brings the effective rate down without a contract.' },
        { type: 'h2', text: 'Caller history in the ticket' },
        { type: 'p', text: 'Every ticket now carries the caller\u2019s previous calls, so your team does not start from zero on a repeat contact.' },
        { type: 'callout', title: 'What we are holding back', text: 'A full outbound dialer. Early tests showed it changed the tone of the product in a way we are not comfortable with yet. It is on the roadmap, not in this release.' },
        { type: 'h2', text: 'What is next in Q3' },
        { type: 'p', text: 'Transcript search across all calls, and a simpler way to hand a call from the AI to a specific person instead of a queue.' },
      ],
    },

    'beauty-brand': {
      author: { name: 'Mia Chen', role: 'Customer Research' },
      cover: 'amber',
      body: [
        { type: 'p', text: 'This brand was not short on support. It was short on a way to close the loop on the first contact.' },
        { type: 'h2', text: 'The problem in one line' },
        { type: 'p', text: 'Every after-sales call went through the same four steps: the customer called, someone took notes, someone else followed up, and the customer called again to check. Three people, four touches, one question.' },
        { type: 'h2', text: 'What we changed' },
        { type: 'ul', items: [
          'The first call ends with a ticket, not a promise.',
          'The customer gets an SMS with the ticket link before they hang up.',
          'The ticket carries the transcript, so the next person does not re-ask.',
        ] },
        { type: 'stat', items: [
          { value: '30s', label: 'to create a ticket' },
          { value: '1', label: 'touch instead of 4' },
          { value: '0', label: 'repeat calls to check status' },
        ] },
        { type: 'h2', text: 'The result' },
        { type: 'p', text: 'Repeat calls dropped within two weeks. The team did not get smaller — they got freed from the part of the job that was only about remembering.' },
        { type: 'quote', text: 'The goal was never fewer people. It was fewer times the customer had to explain the same thing.' },
      ],
    },

    'sorting-work': {
      author: { name: 'Sara Lin', role: 'Product' },
      cover: 'teal',
      body: [
        { type: 'p', text: 'The question is not whether AI reception replaces your team. It is which part of the job it should take off their plate.' },
        { type: 'h2', text: 'The part that should go first' },
        { type: 'p', text: 'Repetition. If a question has the same answer every time, it should not consume a person. That is not a judgment on the work — it is a judgment on the waste.' },
        { type: 'h2', text: 'The part that should stay' },
        { type: 'ul', items: [
          'Anything with an emotional stake — a complaint, a delay, a refund dispute.',
          'Anything with an unusual shape — the case that does not match the pattern.',
          'Anything where the relationship is the point.',
        ] },
        { type: 'h2', text: 'How to decide' },
        { type: 'p', text: 'Sort last month\u2019s tickets by how similar they are to each other. The big cluster at the top is the AI\u2019s job. The long tail is your team\u2019s.' },
        { type: 'quote', text: 'Automation is not about doing more with less. It is about doing the right things with the people you have.' },
        { type: 'h2', text: 'What good looks like' },
        { type: 'p', text: 'Your team should be handling fewer calls and enjoying them more. If the opposite is happening, the AI is sorting the wrong pile.' },
      ],
    },

    'cost-missed-call': {
      author: { name: 'Daniel Wu', role: 'Market Research' },
      cover: 'blue',
      body: [
        { type: 'p', text: 'A missed call has a price. The trouble is that it never shows up on a line item, so most sellers treat it as zero.' },
        { type: 'h2', text: 'The visible and the invisible' },
        { type: 'p', text: 'The visible cost is the order you did not get. The invisible cost is the customer who now has a default answer for "where do I buy this next time."' },
        { type: 'h2', text: 'Three ways to put a number on it' },
        { type: 'ul', items: [
          'Call volume × abandonment rate × your average order value.',
          'Repeat-call rate — every repeat is a first call that failed.',
          'Support hours spent re-asking questions the customer already answered.',
        ] },
        { type: 'stat', items: [
          { value: '1', label: 'missed call, 1 default answer' },
          { value: '3', label: 'repeat touches on average' },
          { value: '0', label: 'of it on your P&L' },
        ] },
        { type: 'h2', text: 'Why it stays invisible' },
        { type: 'p', text: 'Because the loss is a non-event. Nothing was created, nothing was logged, and no dashboard was built to count the nothing.' },
        { type: 'quote', text: 'You cannot fix a number you never wrote down. Start writing it down.' },
      ],
    },

    '3c-brand-sms': {
      author: { name: 'Alex Zhao', role: 'Product' },
      cover: 'amber',
      body: [
        { type: 'p', text: 'The first month is where a new number either becomes part of the workflow or quietly gets ignored. Here is how this 3C brand made it stick.' },
        { type: 'h2', text: 'Week 1: turn it on and say nothing' },
        { type: 'p', text: 'They did not announce the number. They wanted to see what would happen if the only thing that changed was that calls got answered. Volume was low, but every call that came in ended cleanly.' },
        { type: 'h2', text: 'Week 2: put it on the product page' },
        { type: 'p', text: 'Once the first transcripts looked sane, they added the number to the product page. Call volume roughly doubled, and the questions shifted from logistics to product specifics.' },
        { type: 'h2', text: 'Week 3–4: close the loop with SMS' },
        { type: 'ul', items: [
          'Every unresolved call sent a link, not a promise.',
          'The link opened a page with the ticket and the transcript.',
          'Follow-up stopped depending on someone remembering.',
        ] },
        { type: 'stat', items: [
          { value: '12 min', label: 'of phone tag, cut to 1 link' },
          { value: '2x', label: 'call volume after going live' },
          { value: '30 days', label: 'to a new default' },
        ] },
        { type: 'quote', text: 'The number only became real when the SMS made it useful. Before that, it was just a phone line.' },
        { type: 'h2', text: 'What they would tell another seller' },
        { type: 'p', text: 'Do not wait for the perfect script. Turn it on, read the transcripts, and let the first month tell you what to write.' },
      ],
    },

    'voice-tuning': {
      author: { name: 'Alex Zhao', role: 'Product' },
      cover: 'blue',
      body: [
        { type: 'p', text: 'The same words in the wrong voice still sound like a call center. This release gives you four dials you can turn without touching a script.' },
        { type: 'h2', text: 'Tone' },
        { type: 'p', text: 'Pick a register per brand — warm, neutral or brisk. It changes how the agent opens, how it acknowledges a complaint, and how it closes. The wording stays the same; the delivery does not.' },
        { type: 'h2', text: 'Pace' },
        { type: 'p', text: 'Callers on a support line are usually more patient than callers on a sales line. Pace lets you slow the agent down for troubleshooting and speed it up for store hours and directions.' },
        { type: 'h2', text: 'Pronunciation' },
        { type: 'p', text: 'Brand names, model numbers and product lines are the most common source of awkward calls. You can now add a pronunciation entry — write it the way it should sound, and the agent will say it that way every time.' },
        { type: 'ul', items: [
          'Add a brand name once; it applies to every call.',
          'Model numbers can be read digit by digit or as a word.',
          'Changes take effect on the next call, not the next deploy.',
        ] },
        { type: 'h2', text: 'Language mix' },
        { type: 'p', text: 'For sellers whose customers occasionally switch languages mid-call, you can set a primary and a fallback. The agent answers in the primary and follows the caller if they switch.' },
        { type: 'callout', title: 'Where to find it', text: 'Settings → Voice. Everything is per-number, so a second line can sound like a different brand if you want it to.' },
        { type: 'h2', text: 'What we are still working on' },
        { type: 'p', text: 'Accent selection. It is the most requested setting and the one we are least comfortable shipping early — the difference between a regional accent and a caricature is thin, and we would rather get it right than get it out.' },
      ],
    },

    'sms-templates': {
      author: { name: 'Priya Nair', role: 'Product' },
      cover: 'teal',
      body: [
        { type: 'p', text: 'A link sent after the call is a follow-up. A link sent before the caller hangs up is a resolution. This release is about the second one.' },
        { type: 'h2', text: 'The gap we kept seeing' },
        { type: 'p', text: 'In our call recordings, the most common failure was not a wrong answer. It was a right answer that arrived twenty minutes later, in an inbox nobody was watching.' },
        { type: 'h2', text: 'Templates, not free text' },
        { type: 'p', text: 'You write a short template once and attach it to an intent — order status, return, delivery window, warranty. When the agent reaches that point in the call, it sends the template with the caller\u2019s details filled in.' },
        { type: 'ul', items: [
          'The link is sent while the caller is still on the line.',
          'The template decides what the link opens: ticket, tracking, or a booking page.',
          'Every send is logged against the ticket, so nobody has to ask whether it went out.',
        ] },
        { type: 'h2', text: 'Timing rules' },
        { type: 'p', text: 'You can hold a send until the call ends, or fire it immediately. For returns we recommend immediate; for anything that depends on a lookup, holding it a few seconds avoids sending a link to a page that has not updated yet.' },
        { type: 'stat', items: [
          { value: '38%', label: 'fewer repeat calls on templates in beta' },
          { value: '9 sec', label: 'median time from send to open' },
          { value: '0', label: 'extra tools needed' },
        ] },
        { type: 'quote', text: 'The call ends when the customer has what they came for — not when the agent finishes talking.' },
        { type: 'h2', text: 'How to start' },
        { type: 'p', text: 'Pick the three intents that generate the most calls, write one template each, and leave the rest alone for a week. Templates that are not attached to a real intent tend to be templates nobody uses.' },
      ],
    },
  },

  // ===================== 中文 =====================
  zh: {
    'after-hours-calls': {
      author: {
        name: '陈米娅',
        role: '客户研究',
      },
      cover: 'blue',
      body: [
        {
          type: 'p',
          text: '与其猜测漏掉了多少订单，不如先看清哪些来电没有被接住。',
        },
        {
          type: 'p',
          text: '中国团队收工时，美国客户可能刚结束一天工作，正准备确认产品型号、配送安排，或购买前的最后一个疑问。你的网站留了电话，电话也响了，但没人接。',
        },
        {
          type: 'p',
          text: '更值得追问的不是“这算不算丢了一单”，而是：这样的来电有多少、问的是什么、后来有没有人跟进。把这些问题弄清楚，电话才从一个联系方式，变成可以管理的客户接触点。',
        },
        {
          type: 'h2',
          text: '跨时区经营，容易出现一段“无人承接”的时间',
        },
        {
          type: 'p',
          text: '跨境商家已经习惯处理时差：广告按目标市场投放，订单按当地时间产生，客服却通常按中国团队的工作时间排班。客户在品牌官网、包装或其他自有渠道拨打电话时，白天的客服流程未必还在线。',
        },
        {
          type: 'p',
          text: '这类来电不一定都与成交有关。有人询问兼容性，有人催物流，也有人只想确认营业时间。把所有未接来电都算成“流失订单”，会高估机会；把它们一概当作噪音，又会漏掉真正需要帮助的人。',
        },
        {
          type: 'img',
          src: '/blog/after-hours-calls-by-hour.png',
          caption: '连续两到四周的来电记录，按美国当地时间分布。',
        },
        {
          type: 'h2',
          text: '先量出空档，再决定值不值得补',
        },
        {
          type: 'p',
          text: '一个简单的起点是连续记录两到四周的来电：按美国当地时间分布的来电量、未接比例、重复拨打次数、咨询主题，以及后续是否联系成功。对于已接通电话，记录“问到了具体产品吗”“是否需要人工继续处理”“客户是否同意收到后续信息”。',
        },
        {
          type: 'ul',
          items: ['按美国当地时间分布的来电量 —— 空档到底在哪一段。', '未接比例，以及同一个号码重复拨打了几次。', '咨询主题：兼容性、物流、售后，还是只想问营业时间。', '后续是否联系成功，以及是否动用了人工。'],
        },
        {
          type: 'p',
          text: '如果非工作时段几乎没有有效来电，优先优化站点上电话的展示和引导；如果反复出现产品咨询或售后求助，再考虑延长人工值班、语音留言、AI 接待或其他组合。选择应该由自己的来电数据决定。',
        },
        {
          type: 'h2',
          text: 'AI 语音先做好三件事',
        },
        {
          type: 'ul',
          items: ['接住基础问题。AI 围绕商家已审核的 FAQ、商品资料和服务规则回答；遇到型号或政策不确定时，应继续追问，或明确说明暂时无法确认。', '留下可用信息。来电时间、客户问题、提到的产品型号与待解决事项，整理成客服第二天能看懂的记录。只留下“来过电话”，并不能帮助团队继续处理。', '把复杂问题交给人。涉及订单状态、退款、具体承诺或资料缺失时，AI 应说明下一步，而不是为了完成对话给出未经核实的答案。短信、工单或回拨只有在相应能力已开通、客户同意且符合适用规则时使用。'],
        },
        {
          type: 'img',
          src: '/blog/after-hours-calls-three-outcomes.png',
          caption: '一通来电的三种结果：当场解决、生成工单、或发出链接。',
        },
        {
          type: 'h2',
          text: '从一条真实来电开始试验',
        },
        {
          type: 'p',
          text: '挑出最常见的十个来电问题，准备准确的商品资料和回答边界；用真实问法测试型号识别、追问和人工交接；上线后复查未解决通话。先把最常见的一小段流程走通，比一开始承诺“替代全天候客服”更可靠。',
        },
        {
          type: 'p',
          text: '北美电话的价值不只在“有一个号码”，而在于客户拨通后，问题能被理解、被记录，并在需要时被继续处理。',
        },
        {
          type: 'callout',
          title: '一句话总结',
          text: '未接来电是否影响收入，需要数据回答；来电有没有被承接，可以从今天开始管理。',
        },
      ],
    },

    'local-number-trust': {
      author: {
        name: '吴丹尼尔',
        role: '市场研究',
      },
      cover: 'amber',
      body: [
        {
          type: 'p',
          text: '站内响应是平台规则的一部分；品牌电话是另一条客户接触线，两者都要有人负责。',
        },
        {
          type: 'p',
          text: '预算紧张时，商家可能想用一个 AI 电话号码接住所有客户问题：既能少排夜班，也能把服务指标做上去。这个设想有吸引力，但首先要弄清一件事——平台站内消息和品牌自有电话，通常由不同渠道记录，也按不同规则管理。接起一通电话，并不等于回复了平台聊天。',
        },
        {
          type: 'p',
          text: '服务能力可以共享商品知识与处理流程，渠道责任却不能混淆。尤其是经营 Amazon 或 TikTok Shop 的商家，平台后台里的待回复消息仍需按平台要求处理。',
        },
        {
          type: 'h2',
          text: '亚马逊：先看清适用范围和加分机制',
        },
        {
          type: 'p',
          text: '亚马逊近期说明，自发货且由卖家直接负责客服的订单，可以根据三项客服表现获得最多 12 分账户状况评级（AHR）加分：买家联系率低于 0.50%、平均联系响应时间低于 12 小时、买家不满意率低于 4.5%。亚马逊同时明确，低于这些表现标准本身不会限制销售权限。具体数据应以卖家后台 Feedback Manager 的口径为准。',
        },
        {
          type: 'stat',
          items: [
            {
              value: '< 0.50%',
              label: '买家联系率',
            },
            {
              value: '< 12 小时',
              label: '平均联系响应时间',
            },
            {
              value: '< 4.5%',
              label: '买家不满意率',
            },
          ],
        },
        {
          type: 'p',
          text: '这里的响应时间针对买家消息，不是品牌官网电话的接听时长。因此，不能把“AI 接了电话”包装成“自动拿到 AHR 加分”，也不能把这项加分机制说成“漏接一通电话就封店”。',
        },
        {
          type: 'h2',
          text: 'TikTok Shop：考核的是站内聊天的有效回复',
        },
        {
          type: 'p',
          text: 'TikTok Shop 美国站的客服政策要求，过去 7 个自然日内，客户主动发起且需要人工处理的聊天，至少 90% 在 24 小时内得到有意义的回复。单纯发一句“你好”不计入有效回复；纯自动回复也不能替代需要卖家处理的聊天。低于要求可能触发警告或进一步措施，具体以官方政策和店铺后台通知为准。',
        },
        {
          type: 'p',
          text: 'TikTok Shop 的 AHR 还涉及多种政策与履约表现，分数达到相应阈值可能产生平台措施，但不能把所有扣分归因于一次迟回复。品牌电话不替代 Shop Chat，平台外沟通也不能用于绕开平台交易与售后流程。',
        },
        {
          type: 'img',
          src: '/blog/local-number-trust-two-lines.png',
          caption: '两条服务线，两套考核指标。',
        },
        {
          type: 'h2',
          text: '把两条服务线分别排班、分别复盘',
        },
        {
          type: 'p',
          text: '平台站内：指定人员检查消息、订单和投诉；设置后台提醒；对需要人工回复的咨询给出具体答复，并记录升级与截止时间。自动回复只能帮助识别问题、减少重复咨询，不能假定平台一定把它记作有效人工回复。',
        },
        {
          type: 'p',
          text: '品牌自有渠道：统计网站等自有触点的来电量、未接时段和常见问题。AI 电话可以用于基础接待、收集需求和留下交接记录；订单、退款、保修等问题应按企业已核实的规则及相应平台流程处理。',
        },
        {
          type: 'p',
          text: '两条线可以共享一份经过审核的商品知识库，却应分别看响应指标。一个看平台后台的待回复、超时与不满意；一个看来电接通、问题识别、人工跟进和客户同意的后续联系。',
        },
        {
          type: 'h2',
          text: '现在就能做的三项检查',
        },
        {
          type: 'ul',
          items: ['打开平台后台，查看本店真正适用的客服指标与待处理消息。', '拉出最近两周的网站或品牌电话记录，找出无人接听的时段。', '指定一个人负责把复杂咨询从 AI 或值班客服交到能解决问题的人手里。'],
        },
        {
          type: 'p',
          text: '控制客服成本，不应变成放弃服务。先把平台义务做对，再用适合的工具补上自有渠道的空档，才是更可执行的顺序。',
        },
        {
          type: 'h2',
          text: '资料来源',
        },
        {
          type: 'ul',
          items: ['Amazon Seller Forums｜Your Account Health Rating now rewards great customer service', 'TikTok Shop Academy｜Customer Service Policy', 'TikTok Shop Academy｜Requirements for the 24-Hour Response Rate Metric', 'TikTok Shop Academy｜Account Health Rating Requirements', 'Amazon Seller Central｜Communication Guidelines'],
        },
        {
          type: 'callout',
          title: '一句话总结',
          text: '一通电话可以接住客户，但不能替代平台站内那条尚未回复的消息。',
        },
      ],
    },

    'product-faq': {
      author: {
        name: '林莎拉',
        role: '产品',
      },
      cover: 'teal',
      body: [
        {
          type: 'p',
          text: '选品和投放把人带到产品面前，准确的售前回答才能帮助他继续决策。',
        },
        {
          type: 'p',
          text: '跨境卖家熟悉的增长动作是测品、投放、优化页面和寻找下一款潜力商品。这些工作都重要。但当客户已经主动询问“我的设备能不能装进这个包”“这款罩子适用于哪一代型号”时，问题已经变了：此刻他需要的不是更多广告，而是一个可信的回答。',
        },
        {
          type: 'p',
          text: '高意向咨询不等于必然成交。它意味着客户已经提出了明确的决策障碍。如果商家能准确识别并处理这个障碍，至少有机会让对话继续；如果答错型号，热情的推荐反而可能造成退货与失信。',
        },
        {
          type: 'h2',
          text: '一个兼容性问题，往往比一段促销话术更重要',
        },
        {
          type: 'p',
          text: '假设客户为某台音乐设备寻找防尘罩。商品库里有相近型号，也有不同代际和尺寸。客户问的是“适不适用”，客服若把型号听错，再熟练地介绍材质和颜色，也没有解决最重要的问题。',
        },
        {
          type: 'p',
          text: '这只是一个说明流程的示例，不是具体客户案例。正确的顺序应当是确认设备型号与版本，检索已核实的适配资料，再回答“确认适用”“确认不适用”或“目前无法核实”。对无法核实的情况，留下问题并安排人工查证。',
        },
        {
          type: 'img',
          src: '/blog/product-faq-three-answers.png',
          caption: '三种回答：确认适用 / 确认不适用 / 暂无法核实。',
        },
        {
          type: 'h2',
          text: '客户的问题，是商品资料的压力测试',
        },
        {
          type: 'p',
          text: '售前团队可以把咨询分成三类：可以直接回答的标准问题，需要补充一个关键信息的问题，以及现有资料不足、必须交给人的问题。尺寸、颜色和兼容型号看似简单，却最容易因商品表不完整或语音识别错误而走偏。',
        },
        {
          type: 'ul',
          items: ['可以直接回答：尺寸、颜色、配送时间、标准政策。', '需要补充一个信息：先问清“是哪一代”，否则什么都不能确认。', '无法回答：资料缺失、特殊情况，或只有人能给出的承诺。'],
        },
        {
          type: 'p',
          text: '如果同一个型号总被听错，应修正识别词和产品别名；如果客户总问“防水”却资料只写“防泼溅”，应完善可用表述；如果 AI 多次找不到其实已经上架的商品，应检查商品数据而不是只改一句话术。',
        },
        {
          type: 'h2',
          text: '让售前接待形成一个小闭环',
        },
        {
          type: 'ul',
          items: ['先识别客户实际要解决的事。', '缺少型号或使用条件就追问。', '用已核实的商品资料作答，不靠推测。', '推荐商品时说明依据。', '不能确认时转交人工。', '通话结束保留问题、提到的型号和处理结果，供团队复查。'],
        },
        {
          type: 'p',
          text: '商家评估这一流程，不宜只看“AI 回答了多少通”。更有用的指标是：明确型号的比例、需要追问的比例、资料不足导致的转交量，以及人工最终确认了多少次错误推荐。成交数据能关联时，再观察真实订单变化；没有关联数据，就不要直接宣称“接听带来多少转化”。',
        },
        {
          type: 'callout',
          title: '该看什么指标',
          text: '明确型号的来电占比、资料不足导致的转交量、人工拦下的错误推荐次数。',
        },
        {
          type: 'h2',
          text: '从最容易答错的商品开始',
        },
        {
          type: 'p',
          text: '先选一类型号复杂、客户常询问的商品，整理型号、兼容关系、尺寸、SKU 与禁用表述。准备十到二十句客户真实问法，覆盖口音、代际、近似型号和二次纠正。将测试失败的句子回填资料或规则，再复测同类问题。',
        },
        {
          type: 'p',
          text: '这比把全部商品一次性导入后期待 AI 自己“学会销售”更稳妥。售前服务的目标，是减少客户做决定时的疑虑，同时不制造新的错误承诺。',
        },
        {
          type: 'callout',
          title: '一句话总结',
          text: '投放带来机会，准确回答才能让客户放心继续问下去。',
        },
      ],
    },

    'timezone-gap': {
      author: { name: '陈米娅', role: '客户研究' },
      cover: 'blue',
      body: [
        { type: 'p', text: '时差不是一个排班问题。它是一个信任问题，而且偏偏在一天中最糟的时段暴露出来。' },
        { type: 'h2', text: '时差的实际分布' },
        { type: 'p', text: '深圳的卖家晚上 7 点合上电脑。对洛杉矶的顾客来说，那是凌晨 4 点——可订单是顾客当地时间上午 10 点下的，问题到现在还悬着。等到两边都醒着的时候，顾客心里其实已经有答案了。' },
        { type: 'h2', text: '一个美国号码改变的三件事' },
        { type: 'ul', items: [
          '顾客可以在自己的时段打过来，并且能接通。',
          '跟进短信在问题还热的时候到达。',
          '工单的时间戳对得上顾客的那一天，而不是你的。',
        ] },
        { type: 'stat', items: [
          { value: '9 小时', label: '典型的服务重叠缺口' },
          { value: '62%', label: '的夜间来电无人接听' },
          { value: '1 个', label: '号码就能填上缺口' },
        ] },
        { type: 'h2', text: '从哪里开始' },
        { type: 'p', text: '先从你正在丢掉的时段开始，而不是你已经在覆盖的时段。调出上个月的通话记录，按时间排序，找出话量最高、覆盖最低的那个窗口。那个窗口，就是这个号码最先回本的地方。' },
        { type: 'quote', text: '你没法搬动团队的时区。但你可以不再让顾客为它等待。' },
      ],
    },

    '100-calls-test': {
      author: { name: '林莎拉', role: '产品' },
      cover: 'amber',
      body: [
        { type: 'p', text: '我们拿一个真实的商品库跑了 100 通 AI 接待电话，并给每一通打了分。大多数失分，都集中在三种失败模式上。' },
        { type: 'h2', text: '失败一：需要人工介入的问题' },
        { type: 'p', text: '兼容性和保修争议，本质上不是接待问题。当 AI 试图自己解决时，它给出的答案很自信，但有时是错的。解法不是把答案做得更好，而是把转接做得更快。' },
        { type: 'h2', text: '失败二：一口气问两个问题' },
        { type: 'p', text: '来电者常常同时问两件事。模型倾向于回答第一个、丢掉第二个。我们现在会强制在通话结束前做一次确认。' },
        { type: 'h2', text: '失败三：没人指望能读对的号码' },
        { type: 'p', text: '订单号念出来是有噪声的。改成逐位确认之后，错误率明显下降。' },
        { type: 'stat', items: [
          { value: '100 通', label: '逐通打分' },
          { value: '3 种', label: '失败模式' },
          { value: '每种 1 个', label: '针对性修复' },
        ] },
        { type: 'quote', text: '大多数 AI 接待的失误，不是语言问题，而是交接问题。' },
        { type: 'h2', text: '结论' },
        { type: 'p', text: '不要去优化答案，去优化出口。每一通电话都应该结束在一个有人能接手的状态上。' },
      ],
    },

    'four-rings': {
      author: { name: '吴丹尼尔', role: '市场研究' },
      cover: 'teal',
      body: [
        { type: 'p', text: '四声铃响大约是 20 秒。对一个美国来电者来说，这差不多就是“可能只是占线”变成“这家店根本不想接我电话”的那个临界点。' },
        { type: 'h2', text: '为什么是四声铃响' },
        { type: 'p', text: '关于电话放弃率的行为研究显示，挂断在第四声铃响之后会陡增。这不是一条铁律，但是一个很可靠的默认值。' },
        { type: 'h2', text: '一次挂断的代价' },
        { type: 'ul', items: [
          '订单今天不会发生——往往永远不会。',
          '顾客打给了那个接起电话的竞争对手。',
          '你在后台看不到这笔损失，因为挂断不会留下任何记录。',
        ] },
        { type: 'stat', items: [
          { value: '约 20 秒', label: '到第四声铃响' },
          { value: '1/3', label: '在此之前挂断' },
          { value: '0 条', label: '留在你的日志里' },
        ] },
        { type: 'h2', text: '最不舒服的部分' },
        { type: 'p', text: '你没法统计那些你根本没接到的电话。而这恰恰是它为什么在大多数看板上始终隐形的原因。' },
        { type: 'quote', text: '一通漏接的电话，不是一通没接的电话。它是顾客对你做出的一个判断。' },
      ],
    },

    'q3-release': {
      author: { name: '赵亚历克斯', role: '产品' },
      cover: 'blue',
      body: [
        { type: 'p', text: '这个季度上线了三件事，还有一件事我们主动压住了没发。' },
        { type: 'h2', text: '外呼提醒通话' },
        { type: 'p', text: '现在你可以为需要推动的订单安排一通提醒电话——比如发货窗口、待处理的退货、未结清的款项。它用的是和接待线同一个声音、同一个号码，所以不会让顾客觉得是另一家公司。' },
        { type: 'h2', text: '价格调整' },
        { type: 'p', text: '号码月租和每分钟单价没有变化。我们为长期超出基础套餐的账户增加了一档打包分钟数，在不签合同的前提下把实际单价降下来。' },
        { type: 'h2', text: '工单里的来电历史' },
        { type: 'p', text: '每张工单现在都会带上这个来电者之前的通话记录，你的团队在处理重复咨询时不用再从零开始。' },
        { type: 'callout', title: '我们压住了什么', text: '一套完整的外呼拨号器。早期测试显示它会以一种我们还不太放心的方式改变产品的调性。它在路线图上，但不在这个版本里。' },
        { type: 'h2', text: 'Q3 接下来' },
        { type: 'p', text: '全量通话的转写搜索，以及一种更简单的方式——把电话从 AI 直接交给某个具体的人，而不是丢进一个队列。' },
      ],
    },

    'beauty-brand': {
      author: { name: '陈米娅', role: '客户研究' },
      cover: 'amber',
      body: [
        { type: 'p', text: '这个品牌缺的不是客服人力，而是一种能在第一次接触就闭环的方式。' },
        { type: 'h2', text: '一句话说清问题' },
        { type: 'p', text: '每一通售后电话都要走同样四步：顾客来电、有人记录、另一个人跟进、顾客再打来确认。三个人、四次接触、一个问题。' },
        { type: 'h2', text: '我们改了什么' },
        { type: 'ul', items: [
          '第一通电话结束时留下的是工单，而不是一句承诺。',
          '顾客挂断前就收到一条带工单链接的短信。',
          '工单自带通话转写，下一个人不用重新问一遍。',
        ] },
        { type: 'stat', items: [
          { value: '30 秒', label: '生成一张工单' },
          { value: '1 次接触', label: '取代 4 次' },
          { value: '0 通', label: '只为查进度的重复来电' },
        ] },
        { type: 'h2', text: '结果' },
        { type: 'p', text: '重复来电在两周内下降。团队没有变小——他们只是从“纯靠记性”的那部分工作里被解放了出来。' },
        { type: 'quote', text: '目标从来不是更少的人。而是让顾客更少次地重复解释同一件事。' },
      ],
    },

    'sorting-work': {
      author: { name: '林莎拉', role: '产品' },
      cover: 'teal',
      body: [
        { type: 'p', text: '问题不是 AI 接待会不会取代你的团队，而是它应该从团队手里接过哪一部分工作。' },
        { type: 'h2', text: '最该先交出去的部分' },
        { type: 'p', text: '重复。如果一个问题的答案每次都一样，它就不应该占用一个人。这不是对这份工作的评判——而是对浪费的评判。' },
        { type: 'h2', text: '应该留下来的部分' },
        { type: 'ul', items: [
          '任何牵涉情绪的——投诉、延误、退款争议。',
          '任何形态特殊的——不匹配既有模式的那一类。',
          '任何“关系本身就是重点”的。',
        ] },
        { type: 'h2', text: '怎么判断' },
        { type: 'p', text: '把上个月的工单按彼此相似度排一排。顶部那一大团就是 AI 的活儿，长长的尾巴才是你团队的。' },
        { type: 'quote', text: '自动化的意义不是用更少的人做更多的事，而是用现有的人去做对的事。' },
        { type: 'h2', text: '好的状态是什么样' },
        { type: 'p', text: '你的团队应该接更少的电话，而且更愿意接。如果情况正好相反，那说明 AI 分错了堆。' },
      ],
    },

    'cost-missed-call': {
      author: { name: '吴丹尼尔', role: '市场研究' },
      cover: 'blue',
      body: [
        { type: 'p', text: '一通漏接的电话是有价格的。麻烦在于，它从不出现在任何一条账目上，于是大多数卖家默认它等于零。' },
        { type: 'h2', text: '看得见的，和看不见的' },
        { type: 'p', text: '看得见的成本，是你没拿到的那笔订单。看不见的成本，是那个顾客从此对“下次去哪买”有了一个默认答案。' },
        { type: 'h2', text: '三种把它换算成数字的方法' },
        { type: 'ul', items: [
          '通话量 × 放弃率 × 你的客单价。',
          '重复来电率——每一次重复，都是一通失败的首通。',
          '花在重新询问顾客已经答过的问题上的客服工时。',
        ] },
        { type: 'stat', items: [
          { value: '1 通漏接', label: '= 1 个默认答案' },
          { value: '平均 3 次', label: '重复接触' },
          { value: '0', label: '出现在你的损益表上' },
        ] },
        { type: 'h2', text: '为什么它一直隐形' },
        { type: 'p', text: '因为这笔损失是一个“未发生的事件”。没有东西被创造，没有东西被记录，也没有人为这个“没有”建过看板。' },
        { type: 'quote', text: '你没法修复一个你从没记下来的数字。先从记下来开始。' },
      ],
    },

    '3c-brand-sms': {
      author: { name: '赵亚历克斯', role: '产品' },
      cover: 'amber',
      body: [
        { type: 'p', text: '第一个月，决定了一个新号码是会真正进入工作流，还是会悄悄被晾在一边。这家 3C 品牌是这样让它留了下来的。' },
        { type: 'h2', text: '第一周：先开起来，什么都别说' },
        { type: 'p', text: '他们没有对外公布这个号码。他们想看看，如果唯一的变化只是“电话有人接了”，会发生什么。话量不高，但每一通进来的电话都收得很干净。' },
        { type: 'h2', text: '第二周：放到商品页上' },
        { type: 'p', text: '当第一批转写看起来足够可信之后，他们把号码加到了商品页。话量大约翻了一倍，问题也从物流转向了产品细节。' },
        { type: 'h2', text: '第三、四周：用短信闭环' },
        { type: 'ul', items: [
          '每一通没解决的电话，发出去的是一个链接，而不是一句承诺。',
          '链接打开后是一张带转写的工单页。',
          '跟进不再依赖某个人记不记得。',
        ] },
        { type: 'stat', items: [
          { value: '12 分钟', label: '的反复拉扯，缩短成 1 条链接' },
          { value: '2 倍', label: '上线商品页后的通话量' },
          { value: '30 天', label: '形成新默认' },
        ] },
        { type: 'quote', text: '这个号码是在短信让它变得有用之后，才真正成立的。在那之前，它只是一条电话线。' },
        { type: 'h2', text: '他们想告诉其他卖家的话' },
        { type: 'p', text: '不要等一份完美的脚本。先开起来，去读转写，让第一个月告诉你该写什么。' },
      ],
    },

    'voice-tuning': {
      author: { name: '赵亚历克斯', role: '产品' },
      cover: 'blue',
      body: [
        { type: 'p', text: '同样一句话，用错的语气说出来，照样像客服中心。这次更新给了你四个旋钮，不用改脚本就能调。' },
        { type: 'h2', text: '语气' },
        { type: 'p', text: '可以按品牌选一个基调：亲切、中性或干脆。它改变的是 AI 怎么开场、怎么回应抱怨、怎么收尾。用词不变，交付方式变了。' },
        { type: 'h2', text: '语速' },
        { type: 'p', text: '打售后电话的人，通常比打销售电话的人更有耐心。语速可以让你在排查故障时把 AI 放慢，在报营业时间和地址时加快。' },
        { type: 'h2', text: '读音' },
        { type: 'p', text: '品牌名、型号和产品线，是电话里最容易出现尴尬的地方。现在可以加一条读音词条——按它该怎么读来写，AI 每次都会那样读。' },
        { type: 'ul', items: [
          '品牌名加一次，之后所有通话都生效。',
          '型号可以逐位念，也可以当成一个词念。',
          '改完下一通电话就生效，不用等发版。',
        ] },
        { type: 'h2', text: '语言切换' },
        { type: 'p', text: '如果你的顾客偶尔会在通话中途换语言，可以设一个主语言和一个兜底语言。AI 用主语言接起，顾客切换时跟着切。' },
        { type: 'callout', title: '在哪里设置', text: '设置 → 语音。所有配置都是按号码独立的，所以第二条线可以听起来像另一个品牌。' },
        { type: 'h2', text: '还在做的事' },
        { type: 'p', text: '口音选择。这是呼声最高的设置，也是我们最不放心提前上线的——地方口音和刻板模仿之间只隔一层纸，我们宁可做对，也不想先做出来。' },
      ],
    },

    'sms-templates': {
      author: { name: '普里娅·奈尔', role: '产品' },
      cover: 'teal',
      body: [
        { type: 'p', text: '挂断之后才发的链接，叫跟进。在顾客挂断之前发出去的链接，才叫解决。这次更新讲的是后一种。' },
        { type: 'h2', text: '我们反复看到的那个缺口' },
        { type: 'p', text: '在我们的通话记录里，最常见的失败不是答错，而是一个正确答案二十分钟后才到，落在了一个没人看的收件箱里。' },
        { type: 'h2', text: '用模板，而不是随手打字' },
        { type: 'p', text: '你把一条短模板写一次，挂到一个意图上——订单状态、退货、配送时间、保修。当 AI 在通话中走到那一步，它就把顾客的信息填进模板发出去。' },
        { type: 'ul', items: [
          '链接在顾客还没挂断时就发出去了。',
          '模板决定链接打开什么：工单、物流，还是预约页。',
          '每次发送都记在工单上，不用再问“到底发没发”。',
        ] },
        { type: 'h2', text: '发送时机' },
        { type: 'p', text: '你可以把发送压到通话结束后，也可以立刻发。退货建议立刻发；凡是依赖查询结果的，压几秒再发，能避免把一个还没更新的页面推给顾客。' },
        { type: 'stat', items: [
          { value: '38%', label: '测试期重复来电下降' },
          { value: '9 秒', label: '从发出到打开的中位时间' },
          { value: '0', label: '需要额外接入的工具' },
        ] },
        { type: 'quote', text: '一通电话应该在顾客拿到他要的东西时结束，而不是在 AI 说完最后一句话时结束。' },
        { type: 'h2', text: '怎么开始' },
        { type: 'p', text: '挑出带来最多来电的三个意图，每个写一条模板，其余的放一周不管。没挂到真实意图上的模板，往往就是没人用的模板。' },
      ],
    },
  },
};
